/**
 * The two static pages: about (REQ-WEB-032) and methodology (REQ-WEB-033).
 *
 * Static on purpose. They explain the site, so they must still load on the day
 * the API is down — that is when someone is most likely to read them. Nothing
 * here fetches, and nothing here states a count: a number written into a page
 * goes stale silently, and the table already answers "how many" live.
 *
 * The anchors are part of the requirement, not decoration. Other pages and
 * citations link to them, so renaming one breaks a link somewhere else.
 */
import type { ComponentChildren } from "preact";

/**
 * REQ-DM-013's default preference order, highest first.
 *
 * A test (api/tests/test_web_public_pages.py) reads this list and the spec's
 * own sentence and fails if they differ, so the page cannot drift from the rule
 * the database applies.
 */
export const SOURCE_ORDER = [
  "manufacturer",
  "manual",
  "press_release",
  "review",
  "measured",
  "retailer",
  "other",
] as const;

const SOURCE_LABEL: Record<(typeof SOURCE_ORDER)[number], string> = {
  manufacturer: "the manufacturer's own specification",
  manual: "the lens's printed manual",
  press_release: "the manufacturer's press release",
  review: "an independent review",
  measured: "a measurement",
  retailer: "a retailer's listing",
  other: "anything else, including figures lens-specs derives itself",
};

/** The site's contact address (REQ-WEB-032, D26). No personal name is published. */
export const CONTACT = "about@lens-specs.com";
const REPORT_SUBJECT = "Wrong figure on lens-specs";

export const ATTRIBUTION = "Lens specifications from lens-specs.com, licensed under CC BY 4.0.";
const CC_BY = "https://creativecommons.org/licenses/by/4.0/";

/** `/about` → "about", `/methodology` → "methodology", anything else → null. */
export function staticPage(path: string): "about" | "methodology" | null {
  const m = /^\/(about|methodology)\/?$/.exec(path);
  return m ? (m[1] as "about" | "methodology") : null;
}

/**
 * Is this one of the site's addresses? The list, a lens, the comparison, and
 * the two static pages. Anything else used to render the list, so a mistyped
 * or retired address (`/submit`) looked like a working page (REQ-WEB-013, #217).
 * nginx.conf answers the same set with 200 and everything else with 404.
 */
export function knownRoute(path: string): boolean {
  return /^\/(?:|lens\/[A-Za-z0-9-]+\/?|compare\/?|about\/?|methodology\/?)$/.test(path);
}

/**
 * Links every view carries, so the licence is reachable from every page
 * (REQ-WEB-016) and so is the explanation (REQ-WEB-032).
 *
 * Plain links, full page loads. These pages sit outside the table's URL-held
 * query entirely, which is simpler than teaching its history handling about
 * two pages that have no state.
 */
export function SiteLinks() {
  return (
    <nav class="site-links" aria-label="About this site">
      <a href="/">Lenses</a>
      <a href="/about">About</a>
      <a href="/methodology">Methodology</a>
      <a href="/about#licence">Licence &amp; reuse</a>
      <a href="/about#contact">Contact</a>
    </nav>
  );
}

/**
 * The one-line disclaimer every view's footer carries (REQ-WEB-043, D52). The
 * full wording is the about page's #disclaimer section.
 */
export function Disclaimer() {
  return (
    <p class="disclaimer">
      Specs come from the cited sources and may contain errors. Check with the maker before you
      buy. Not affiliated with any lens maker. <a href="/about#disclaimer">Disclaimer</a>
    </p>
  );
}

/** For views that have no footer of their own: detail and compare. */
export function SiteFooter() {
  return (
    <footer class="footer shell">
      <p>Data under CC BY 4.0, free to reuse with credit.</p>
      <Disclaimer />
      <SiteLinks />
    </footer>
  );
}

function Page(props: {
  title: string;
  lede: string;
  children: ComponentChildren;
  /** Extra line at the very bottom of the footer. Only About uses this (D32). */
  footnote?: ComponentChildren;
}) {
  return (
    <div class="shell">
      <header class="masthead">
        <a class="wordmark wordmark-link" href="/">
          lens<span class="wordmark-dot">·</span>specs
        </a>
      </header>
      <main class="page">
        <h1 class="page-title">{props.title}</h1>
        <p class="page-lede">{props.lede}</p>
        {props.children}
      </main>
      <footer class="footer">
        <Disclaimer />
        <SiteLinks />
        {props.footnote}
      </footer>
    </div>
  );
}

function Section(props: { id: string; title: string; children: ComponentChildren }) {
  return (
    <section id={props.id} class="page-section" aria-labelledby={`${props.id}-h`}>
      <h2 id={`${props.id}-h`}>{props.title}</h2>
      {props.children}
    </section>
  );
}

export function About() {
  return (
    <Page
      title="About lens-specs"
      lede="A database of camera lens specifications where every figure says where it came from."
      footnote={
        // So "am I actually running the new build?" is answerable by looking,
        // rather than by clearing caches and hoping. Moved here from the list
        // footer (D32, REQ-WEB-030 amended): the list, lens and compare pages
        // show no text about the implementation.
        <p class="footer-meta">
          <span class="build-id" title="Build identifier">
            build {__BUILD_ID__}
          </span>
        </p>
      }
    >
      <Section id="citations" title="Every value is cited">
        <p>
          Each number here carries its own source: who published it, what kind of source it is,
          and when it was checked. Tap or select any value to see it.
        </p>
        <p>
          Sources are recorded for each value separately. A lens's weight might come from its
          maker and its length from a review, and each shows its own source.
        </p>
      </Section>

      <Section id="disagreement" title="When sources disagree">
        <p>
          Sources sometimes disagree. When they do, every value is kept, one is shown by default,
          and the cell is marked so you can see the others and where each came from. When
          independent publishers agree, that is shown too.
        </p>
        <p>
          How the default is chosen, and what counts as agreement, is set out on the{" "}
          <a href="/methodology">methodology</a> page.
        </p>
      </Section>

      <Section id="contributing" title="Where the data comes from">
        <p>
          Values come from published sources, mostly the makers' own specification pages, and are
          checked before they appear. When a value is corrected, the old one is kept in its
          history.
        </p>
        <p>
          You can't submit values yourself yet; that is planned. Until then, if a figure looks
          wrong, please <a href="#contact">tell us</a>.
        </p>
      </Section>

      <Section id="licence" title="Licence and reuse">
        <p>
          The dataset is published under{" "}
          <a href={CC_BY} rel="license">
            Creative Commons Attribution 4.0 (CC BY 4.0)
          </a>
          . You may copy, adapt and build on it, commercially or not, as long as you credit
          lens-specs.
        </p>
        <p>
          The whole dataset is published for download and regenerated automatically:{" "}
          <a href="/data/lens-specs.csv" download="lens-specs.csv">
            CSV, one row per lens
          </a>
          ;{" "}
          <a href="/data/lens-specs-values.csv" download="lens-specs-values.csv">
            CSV, every value with its source
          </a>
          , including the ones sources disagree about; and{" "}
          <a href="/data/lens-specs.json" download="lens-specs.json">
            JSON
          </a>
          . Each file states its licence, version and the time it was generated.
        </p>
        <p>A credit line you can use:</p>
        <blockquote class="attribution">{ATTRIBUTION}</blockquote>
        <p>
          Individual specifications are facts, and the manufacturers remain the authority on
          their own products. What the licence covers is this compilation: the values, their
          citations, and the record of where sources disagree.
        </p>
      </Section>

      <Section id="disclaimer" title="Disclaimer">
        <p>
          <strong>Accuracy.</strong> Information here is provided as is, without any warranty. It
          may be incomplete, out of date or wrong. Confirm anything important with the
          manufacturer.
        </p>
        <p>
          <strong>Citations.</strong> A citation shows where a value came from. It does not mean the
          source endorses lens-specs, is affiliated with it, or has made any statement to it.
        </p>
        <p>
          <strong>Trademarks.</strong> Product and brand names belong to their owners and are used
          only to identify products.
        </p>
        <p>
          <strong>Liability.</strong> lens-specs is not liable for any loss arising from use of this
          information, to the extent the law allows.
        </p>
        <p>
          <strong>Corrections.</strong> If something is wrong, please{" "}
          <a href="#contact">tell us</a>.
        </p>
      </Section>

      <Section id="contact" title="Contact, and reporting a wrong figure">
        <p>
          Write to <a href={`mailto:${CONTACT}`}>{CONTACT}</a>.
        </p>
        <p>
          If a figure looks wrong, please say which lens and which spec, and where you saw a
          different value if you can. A link to the page that states it helps most, because every
          correction here needs a citation too.{" "}
          <a href={`mailto:${CONTACT}?subject=${encodeURIComponent(REPORT_SUBJECT)}`}>
            Report a wrong figure
          </a>
          .
        </p>
      </Section>
    </Page>
  );
}

export function NotFound() {
  return (
    <Page title="Page not found" lede="There's nothing at this address. It may be mistyped, or it may have moved.">
      <p>
        <a href="/">Go to the lens list</a>, or read <a href="/about">what this site is</a>.
      </p>
    </Page>
  );
}

export function Methodology() {
  return (
    <Page
      title="Methodology"
      lede="The rules that decide what a cell shows, and how to read it."
    >
      <Section id="preference" title="Which value is shown">
        <p>
          When more than one accepted source gives a value, one is shown by default. Unless a
          reviewer has chosen, the default comes from the kind of source, in this order:
        </p>
        <ol class="source-order">
          {SOURCE_ORDER.map((t) => (
            <li key={t}>{SOURCE_LABEL[t]}</li>
          ))}
        </ol>
        <p>
          Between two sources of the same kind, the more recently checked one wins.
        </p>
        <p>
          This order only decides which value is shown first. A careful measurement can be more
          accurate than a maker's rounded figure, so the other values stay visible.
        </p>
        <p>
          A reviewer can choose a different value on purpose. That choice is recorded as a
          person's decision and is never changed automatically, even if a higher-ranked source
          arrives later. A default that nobody chose does move when a higher-ranked source
          appears.
        </p>
      </Section>

      <Section id="agreement" title="When two values agree">
        <p>
          Sources print figures to different precision, and that is judged as printed. A source
          that says <strong>0.34×</strong> is claiming somewhere from 0.335 to 0.345; one that
          says <strong>0.3448×</strong> is claiming a much narrower range. The first is simply less
          precise, so the two are shown as agreeing.
        </p>
        <p>
          Ranges that only touch do not count. <strong>372 g</strong> and <strong>373 g</strong>{" "}
          are a disagreement, and are shown as one.
        </p>
        <p>
          Agreement from two pages by the same publisher counts once. Two independent publishers
          agreeing is shown as corroboration.
        </p>
      </Section>

      <Section id="missing" title="Blank, unknown and not applicable">
        <dl class="cell-states">
          <dt>a value</dt>
          <dd>At least one accepted, cited source gives it.</dd>
          <dt>—</dt>
          <dd>Nobody has cited a value yet. It is a gap to fill, never zero.</dd>
          <dt>n/a</dt>
          <dd>The specification cannot apply to this lens.</dd>
        </dl>
      </Section>

      <Section id="history" title="Nothing is overwritten">
        <p>
          Values are never edited in place. A correction is recorded as a new value that
          supersedes the old one, so every earlier state can be reconstructed and a bad import can
          be undone without touching anything else.
        </p>
      </Section>

      <Section id="derived" title="Values lens-specs works out itself">
        <p>
          A few values can be calculated from others the database already holds. Where they are,
          the calculated figure is stored like any other value and cites lens-specs as its source.
          Because that source ranks last in the order above, a value published by anyone else
          replaces it automatically. Where an input is missing, nothing is calculated: a guess is
          worse than a gap.
        </p>

        <h3 id="lens-category">Lens category</h3>
        <p>
          A lens's category describes its field of view, which is its focal length judged against
          the format it covers. The focal length is first converted to its 35 mm equivalent:
        </p>
        <div class="table-scroll">
          <table class="rule-table">
            <thead>
              <tr><th scope="col">coverage</th><th scope="col">crop factor</th></tr>
            </thead>
            <tbody>
              <tr><td>full frame</td><td>1.0</td></tr>
              <tr><td>APS-C</td><td>1.5</td></tr>
              <tr><td>APS-C, on Canon mounts</td><td>1.6</td></tr>
              <tr><td>Four Thirds</td><td>2.0</td></tr>
              <tr><td>medium format</td><td>0.79</td></tr>
            </tbody>
          </table>
        </div>
        <p>
          The crop factor belongs to the camera system, not only to the format: Canon's APS-C
          bodies are 1.6 where most others' are 1.5, so each mount records its own factor for each
          format its cameras come in, and a lens's own category uses its mount's. Where a mount
          has none recorded, the figure in the first table is used.
        </p>
        <p>
          When you choose a camera on the list, categories are judged for that camera: the lens's
          focal length is multiplied by the camera format's crop factor on that mount, so a
          full-frame 24 mm lens on an APS-C camera is a 36 mm equivalent, a standard lens, not a
          wide one. A lens made for a smaller format than your camera is judged at its own
          format's factor, because the camera crops to it. Where no factor is recorded, the
          lens's category for its own format is shown instead. With no camera chosen, every lens
          shows the category for its own format, and each lens page says which format its
          category refers to.
        </p>
        <p>Primes are judged on their single equivalent focal length:</p>
        <div class="table-scroll">
          <table class="rule-table">
            <thead>
              <tr><th scope="col">equivalent focal length</th><th scope="col">category</th></tr>
            </thead>
            <tbody>
              <tr><td>under 24 mm</td><td>ultra wide</td></tr>
              <tr><td>24 to under 35 mm</td><td>wide</td></tr>
              <tr><td>35 to under 60 mm</td><td>standard</td></tr>
              <tr><td>60 to under 135 mm</td><td>short telephoto</td></tr>
              <tr><td>135 to under 300 mm</td><td>telephoto</td></tr>
              <tr><td>300 mm and above</td><td>super telephoto</td></tr>
            </tbody>
          </table>
        </div>
        <p>Zooms are judged on their equivalent range, first matching row wins:</p>
        <div class="table-scroll">
          <table class="rule-table">
            <thead>
              <tr><th scope="col">condition</th><th scope="col">category</th></tr>
            </thead>
            <tbody>
              <tr><td>long end ÷ short end is 5 or more</td><td>superzoom</td></tr>
              <tr><td>long end is 40 mm or less</td><td>wide zoom</td></tr>
              <tr><td>short end is 70 mm or more</td><td>telephoto zoom</td></tr>
              <tr><td>otherwise</td><td>standard zoom</td></tr>
            </tbody>
          </table>
        </div>
        <p>
          These boundaries are a convention. Nothing physical makes 34 mm wide and 35 mm standard. They follow the lines photographers and retailers already draw, and
          they are published so that anyone who would draw them elsewhere can see exactly where
          they sit.
        </p>
      </Section>
    </Page>
  );
}
